import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { ImagePreviewStrip, useGalleryImages } from "../../../../shared/components/gallery";
import ReportFile from "../../../../shared/ReportFile";
import { PublicServiceRequestFile } from "../../../serviceBoard/models/models";

interface RfsAttachmentsCardProps {
  files: PublicServiceRequestFile[];
}

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;

const isImageFile = (file: PublicServiceRequestFile): boolean => {
  return IMAGE_EXTENSIONS.test(file.name) || file.content?.startsWith("data:image/");
};

const RfsAttachmentsCard = ({ files }: RfsAttachmentsCardProps) => {
  const { translate } = useTranslation(["servicing"]);

  const safeFiles = files || [];
  const imageFiles = safeFiles.filter(isImageFile);
  const nonImageFiles = safeFiles.filter((f) => !isImageFile(f));
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  if (safeFiles.length === 0) return null;

  return (
    <Box>
      {/* Section header */}
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 700,
          fontFamily: dt.font,
          textTransform: "uppercase",
          letterSpacing: "1.2px",
          color: dt.textMuted,
          mb: "10px",
        }}
      >
        {translate("servicing:attachments")}
      </Box>

      {/* Image thumbnails — 3 + "+N" strip, click opens lightbox */}
      {galleryImages.length > 0 && (
        <Box sx={{ mb: nonImageFiles.length > 0 ? 1.5 : 0 }}>
          <ImagePreviewStrip images={galleryImages} maxVisible={3} />
        </Box>
      )}

      {/* Non-image files */}
      {nonImageFiles.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {nonImageFiles.map((file, index) => (
            <ReportFile
              key={index}
              file={{ name: file.name, content: file.content, size: file.size || 0 }}
              fileIndex={index}
              canDelete={false}
              canDownload={true}
              isNew={false}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RfsAttachmentsCard;
